import React, { useState, useEffect } from 'react';
import styles from './JurisdictionSelector.module.css';

interface Jurisdiction {
  id: string;
  name: string;
  children?: Jurisdiction[];
}

interface JurisdictionSelectorProps {
  jurisdictions: Jurisdiction[];
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Record<string, number>;
}

const JurisdictionSelector: React.FC<JurisdictionSelectorProps> = ({
  jurisdictions,
  selected,
  onChange,
  counts = {},
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Preserve existing expanded state when possible. Only add new groups
    // as expanded by default so the UI doesn't unexpectedly collapse when
    // the `jurisdictions` prop updates (e.g. after filtering/search).
    setExpanded(prev => {
      const nextExpanded: Record<string, boolean> = { ...prev };
      jurisdictions.forEach(j => {
        if (j.children && j.children.length > 0 && !(j.id in nextExpanded)) {
          nextExpanded[j.id] = true;
        }
      });
      return nextExpanded;
    });
  }, [jurisdictions]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Collect the selectable leaf ids under a node. Group/parent rows carry a
  // synthetic id (the `type` slug, e.g. "country") that is NOT a real
  // jurisdiction code, so checking a parent must translate to selecting all of
  // its leaf children — otherwise the backend filters on a code that matches
  // nothing and the user sees zero results ("filters not working").
  const leafIds = (node: Jurisdiction): string[] => {
    if (node.children && node.children.length > 0) {
      return node.children.flatMap(leafIds);
    }
    return [node.id];
  };

  const toggleJurisdiction = (node: Jurisdiction) => {
    const ids = leafIds(node);
    const allSelected = ids.length > 0 && ids.every(id => selected.includes(id));
    if (allSelected) {
      onChange(selected.filter(item => !ids.includes(item)));
    } else {
      const next = new Set(selected);
      ids.forEach(id => next.add(id));
      onChange(Array.from(next));
    }
  };

  const renderJurisdiction = (jurisdiction: Jurisdiction, level = 0) => {
    const hasChildren = jurisdiction.children && jurisdiction.children.length > 0;
    // A node is checked when all of its leaf descendants are selected (so a
    // parent reflects "all children selected"); a leaf is checked when its own
    // id is selected.
    const nodeLeafIds = leafIds(jurisdiction);
    const isChecked =
      nodeLeafIds.length > 0 && nodeLeafIds.every(id => selected.includes(id));
    // While searching, force groups open so matching children are always
    // visible even if the user previously collapsed the group.
    const isExpanded = searchTerm.trim() !== '' || expanded[jurisdiction.id];
    const getCountForNode = (node: Jurisdiction) => {
      if (node.children && node.children.length > 0) {
        return node.children.reduce((sum, child) => sum + (counts[child.id] || 0), 0);
      }
      return counts[node.id] || 0;
    };
    const nodeCount = getCountForNode(jurisdiction);

    return (
      <div key={jurisdiction.id} className={styles.jurisdictionItem}>
        <div 
          className={styles.jurisdictionLabel}
          style={{ paddingLeft: `${level * 16}px` }}
        >
          {hasChildren && (
            <button 
              onClick={() => toggleExpand(jurisdiction.id)}
              className={styles.expandButton}
            >
              {isExpanded ? '−' : '+'}
            </button>
          )}
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleJurisdiction(jurisdiction)}
              className={styles.checkbox}
            />
            <span className={styles.jurisdictionName}>{jurisdiction.name}</span>
            <span className={styles.countBadge}>{nodeCount}</span>
          </label>
        </div>
        {hasChildren && isExpanded && (
          <div className={styles.childrenContainer}>
            {jurisdiction.children?.map(child => 
              renderJurisdiction(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter groups AND their children by the search term. A group whose own name
  // matches keeps all its children; otherwise only the children that match are
  // shown — so typing "uni" doesn't drag in non-matching siblings (Uruguay,
  // Uzbekistan, …) just because the group also contains "United Kingdom".
  const term = searchTerm.trim().toLowerCase();
  const filteredJurisdictions = !term
    ? jurisdictions
    : jurisdictions.reduce<Jurisdiction[]>((acc, jurisdiction) => {
        const nameMatches = jurisdiction.name.toLowerCase().includes(term);
        if (nameMatches) {
          acc.push(jurisdiction);
        } else if (jurisdiction.children && jurisdiction.children.length > 0) {
          const matchingChildren = jurisdiction.children.filter(child =>
            child.name.toLowerCase().includes(term)
          );
          if (matchingChildren.length > 0) {
            acc.push({ ...jurisdiction, children: matchingChildren });
          }
        }
        return acc;
      }, []);

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search jurisdictions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>
      <div className={styles.jurisdictionList}>
        {filteredJurisdictions.map(jurisdiction => 
          renderJurisdiction(jurisdiction)
        )}
      </div>
    </div>
  );
};

export default JurisdictionSelector;
